import { lenis } from '../utilities/lenis';

function initNavigation() {
  if (!initNavigation._hasResizeListener) {
    initNavigation._hasResizeListener = true;
    window.addEventListener('resize', debounce(initNavigation, 200));
  }
  const isMobile = window.innerWidth < 992;
  if (isMobile && initNavigation._lastMode !== 'mobile') {
    initMobileMenu();
    initNavigation._lastMode = 'mobile';
  } else if (!isMobile && initNavigation._lastMode !== 'desktop') {
    initDesktopDropdowns();
    initNavigation._lastMode = 'desktop';
  }
}
function debounce(fn, delay) {
  let timer;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(fn, delay);
  };
}
function initMobileMenu() {
  const btn = document.querySelector('[data-menu-button]');
  const nav = document.querySelector('[data-menu-status]');
  if (!btn || !nav) return;
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-controls', 'mobile-navigation');
  nav.setAttribute('id', 'mobile-navigation');
  nav.setAttribute('role', 'navigation');
  nav.setAttribute('aria-label', 'Main navigation');
  if (!btn._mobileClick) {
    btn._mobileClick = true;
    const header = document.querySelector('.c-header');
    btn.addEventListener('click', () => {
      const open = nav.dataset.menuStatus === 'open';
      nav.dataset.menuStatus = open ? 'closed' : 'open';
      btn.setAttribute('aria-expanded', !open);
      // Close all dropdowns when closing the menu
      if (open) {
        Array.from(document.querySelectorAll('[data-dropdown-toggle]')).forEach(toggle => {
          toggle.dataset.dropdownToggle = 'closed';
          toggle.setAttribute('aria-expanded', 'false');
        });
        if (header) header.classList.remove('menu-logo-sprout');
        if (lenis && typeof lenis.stop === 'function') {
          lenis.start();
        }
      } else {
        if (header && !header.classList.contains('scrolled')) {
          header.classList.add('menu-logo-sprout');
        }
        if (lenis && typeof lenis.stop === 'function') {
          lenis.stop();
        }
      }
    });
  }
  Array.from(document.querySelectorAll('[data-dropdown-toggle]')).forEach((toggle, i) => {
    const dd = toggle.nextElementSibling;
    if (!dd || !dd.classList.contains('c-nav-dropdown')) return;
    if (toggle._mobileDropdownInit) return;
    toggle._mobileDropdownInit = true;

    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-haspopup', 'true');
    toggle.setAttribute('aria-controls', `dropdown-${i}`);
    dd.setAttribute('id', `dropdown-${i}`);
    dd.setAttribute('role', 'menu');
    dd.querySelectorAll('.c-nav-dropdown__link')
      .forEach(link => link.setAttribute('role', 'menuitem'));
    toggle.addEventListener('click', () => {
      const open = toggle.dataset.dropdownToggle === 'open';
      Array.from(document.querySelectorAll('[data-dropdown-toggle]'))
        .forEach(other => {
          if (other !== toggle) {
            other.dataset.dropdownToggle = 'closed';
            other.setAttribute('aria-expanded', 'false');
            if (other === document.activeElement) other.blur();
          }
        });
      toggle.dataset.dropdownToggle = open ? 'closed' : 'open';
      toggle.setAttribute('aria-expanded', !open);
      if (open && toggle === document.activeElement) toggle.blur();
    });
  });
}
function initDesktopDropdowns() {
  const toggles = Array.from(document.querySelectorAll('[data-dropdown-toggle]'));
  const links = Array.from(document.querySelectorAll('.c-nav-link:not([data-dropdown-toggle])'));
  const navBg = document.querySelector('.c-nav-bg');
  const HOVER_SWITCH_DELAY = 0;
  const HOVER_CLOSE_DELAY = 200;
  
  // Function to update nav background height based on open dropdown
  const updateNavBgHeight = () => {
    if (!navBg) return;
    
    // Find the currently open dropdown (by data attribute or by visibility)
    let openToggle = toggles.find(t => t.dataset.dropdownToggle === 'open');
    
    // If no dropdown marked as open, check for any visible dropdowns (CSS hover edge case)
    if (!openToggle) {
      openToggle = toggles.find(toggle => {
        const dd = toggle.nextElementSibling;
        if (!dd || !dd.classList.contains('c-nav-dropdown')) return false;
        const computedStyle = window.getComputedStyle(dd);
        return computedStyle.opacity !== '0' && 
               computedStyle.visibility !== 'hidden' &&
               computedStyle.pointerEvents !== 'none';
      });
      
      // If found a visible dropdown, mark it as open
      if (openToggle) {
        const dd = openToggle.nextElementSibling;
        openToggle.dataset.dropdownToggle = 'open';
        openToggle.setAttribute('aria-expanded', 'true');
        if (dd) dd.setAttribute('aria-hidden', 'false');
      }
    }
    
    if (!openToggle) {
      // No dropdown open, reset height
      navBg.style.height = '0';
      return;
    }
    
    const openDropdown = openToggle.nextElementSibling;
    if (!openDropdown || !openDropdown.classList.contains('c-nav-dropdown')) {
      navBg.style.height = '0';
      return;
    }
    
    // Get the header height
    const headerHeight = document.querySelector('.c-header-component')?.offsetHeight || 0;
    
    // Calculate dropdown height including padding
    // Use getBoundingClientRect() which includes padding, or manually add padding
    const computedStyle = window.getComputedStyle(openDropdown);
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    
    // Get the content height (scrollHeight includes padding)
    // Or use getBoundingClientRect().height which includes all padding
    const dropdownRect = openDropdown.getBoundingClientRect();
    const dropdownHeight = dropdownRect.height + paddingBottom;
        
    // Set nav-bg height to header height + dropdown height
    // navBg.style.height = `${headerHeight + dropdownHeight}px`;
    navBg.style.height = `${dropdownHeight}px`;
  };
  
  const closeDropdown = (toggle, dropdown) => {
    if (toggle._openTimer) {
      clearTimeout(toggle._openTimer);
      toggle._openTimer = null;
    }
    toggle.dataset.dropdownToggle = 'closed';
    toggle.setAttribute('aria-expanded', 'false');
    dropdown.setAttribute('aria-hidden', 'true');
    // Update nav bg height after closing
    setTimeout(updateNavBgHeight, 0);
  };
  const openDropdown = (toggle, dropdown, focusFirstLink = false) => {
    toggle.dataset.dropdownToggle = 'open';
    toggle.setAttribute('aria-expanded', 'true');
    dropdown.setAttribute('aria-hidden', 'false');
    if (focusFirstLink) {
      const first = dropdown.querySelector('.c-nav-dropdown__link');
      if (first) first.focus();
    }
    // Update nav bg height after opening (with small delay to ensure layout is complete)
    setTimeout(updateNavBgHeight, 50);
  };
  const closeAllDropdowns = exceptToggle => {
    toggles.forEach(other => {
      if (!exceptToggle || other !== exceptToggle) {
        const otherDropdown = other.nextElementSibling;
        if (otherDropdown) closeDropdown(other, otherDropdown);
      }
    });
  };
  const cancelScheduledClose = toggle => {
    if (toggle._closeTimer) {
      clearTimeout(toggle._closeTimer);
      toggle._closeTimer = null;
    }
  };
  const scheduleClose = (toggle, dropdown) => {
    cancelScheduledClose(toggle);
    toggle._closeTimer = setTimeout(() => {
      closeDropdown(toggle, dropdown);
    }, HOVER_CLOSE_DELAY);
  };
  toggles.forEach((toggle, i) => {
    const dd = toggle.nextElementSibling;
    if (!dd || !dd.classList.contains('c-nav-dropdown') || toggle._desktopInit) return;
    toggle._desktopInit = true;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-haspopup', 'true');
    toggle.setAttribute('aria-controls', `desktop-dropdown-${i}`);
    dd.setAttribute('id', `desktop-dropdown-${i}`);
    dd.setAttribute('role', 'menu');
    dd.setAttribute('aria-hidden', 'true');
    dd.querySelectorAll('.c-nav-dropdown__link')
      .forEach(link => link.setAttribute('role', 'menuitem'));
    toggle.addEventListener('click', e => {
      e.preventDefault();
      closeAllDropdowns(toggle);
      const open = toggle.dataset.dropdownToggle !== 'open';
      if (open) {
        openDropdown(toggle, dd, true);
      } else {
        closeDropdown(toggle, dd);
      }
    });
    toggle.addEventListener('mouseenter', () => {
      cancelScheduledClose(toggle);
      const anyOpen = toggles.some(x => x.dataset.dropdownToggle === 'open');
      closeAllDropdowns(toggle);
      if (anyOpen) {
        clearTimeout(toggle._openTimer);
        toggle._openTimer = setTimeout(() => {
          openDropdown(toggle, dd);
        }, HOVER_SWITCH_DELAY);
      } else {
        openDropdown(toggle, dd);
      }
    });
    toggle.addEventListener('mouseleave', e => {
      if (e.relatedTarget && dd.contains(e.relatedTarget)) return;
      scheduleClose(toggle, dd);
    });
    dd.addEventListener('mouseenter', () => {
      cancelScheduledClose(toggle);
    });
    dd.addEventListener('mouseleave', e => {
      if (e.relatedTarget && toggle.contains(e.relatedTarget)) return;
      scheduleClose(toggle, dd);
    });
    toggle.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle.click();
      } else if (e.key === 'Escape') {
        toggle.dataset.dropdownToggle = 'closed';
        toggle.setAttribute('aria-expanded', 'false');
        dd.setAttribute('aria-hidden', 'true');
        toggle.focus();
      }
    });
    dd.addEventListener('keydown', e => {
      const items = Array.from(dd.querySelectorAll('.c-nav-dropdown__link'));
      const idx = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        items[(idx + 1) % items.length].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        items[(idx - 1 + items.length) % items.length].focus();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        toggle.dataset.dropdownToggle = 'closed';
        toggle.setAttribute('aria-expanded', 'false');
        dd.setAttribute('aria-hidden', 'true');
        toggle.focus();
      } else if (e.key === 'Tab' && !dd.contains(e.relatedTarget)) {
        toggle.dataset.dropdownToggle = 'closed';
        toggle.setAttribute('aria-expanded', 'false');
        dd.setAttribute('aria-hidden', 'true');
      }
    });
  });
  links.forEach(link => {
    link.addEventListener('mouseenter', () => {
      toggles.forEach(toggle => {
        cancelScheduledClose(toggle);
        const dd = toggle.nextElementSibling;
        if (dd) closeDropdown(toggle, dd);
      });
    });
  });
  document.addEventListener('click', e => {
    const inside = toggles.some(toggle => {
      const dd = toggle.nextElementSibling;
      return toggle.contains(e.target) || (dd && dd.contains(e.target));
    });
    if (!inside) {
      toggles.forEach(toggle => {
        cancelScheduledClose(toggle);
        const dd = toggle.nextElementSibling;
        if (dd) closeDropdown(toggle, dd);
      });
    }
  });
  
  // Close all dropdowns on scroll
  window.addEventListener('scroll', () => {
    const anyOpen = toggles.some(t => t.dataset.dropdownToggle === 'open');
    if (anyOpen) {
      closeAllDropdowns();
      navBg.style.height = '0';
    }
  }, { passive: true });

  // Listen for resize events to recalculate height
  window.addEventListener('resize', debounce(() => {
    const anyOpen = toggles.some(t => t.dataset.dropdownToggle === 'open');
    if (anyOpen) {
      updateNavBgHeight();
    }
  }, 100));
  
  // Use ResizeObserver to watch for dropdown size changes
  // This handles cases where dropdown content might change dynamically
  if (window.ResizeObserver && navBg) {
    const resizeObserver = new ResizeObserver(() => {
      const anyOpen = toggles.some(t => t.dataset.dropdownToggle === 'open');
      if (anyOpen) {
        updateNavBgHeight();
      }
    });
    
    // Observe all dropdowns
    toggles.forEach(toggle => {
      const dd = toggle.nextElementSibling;
      if (dd && dd.classList.contains('c-nav-dropdown')) {
        resizeObserver.observe(dd);
      }
    });
  }
  
  // Check for dropdowns that are already visible via CSS hover on page load
  // This handles the edge case where user refreshes while hovering
  const checkForVisibleDropdowns = () => {
    toggles.forEach(toggle => {
      const dd = toggle.nextElementSibling;
      if (!dd || !dd.classList.contains('c-nav-dropdown')) return;
      
      // Check if dropdown is visible (opacity > 0 or has visibility visible)
      const computedStyle = window.getComputedStyle(dd);
      const isVisible = computedStyle.opacity !== '0' && 
                       computedStyle.visibility !== 'hidden' &&
                       computedStyle.pointerEvents !== 'none';
      
      if (isVisible && toggle.dataset.dropdownToggle !== 'open') {
        // Dropdown is visible via CSS hover but JS hasn't set it as open
        toggle.dataset.dropdownToggle = 'open';
        toggle.setAttribute('aria-expanded', 'true');
        dd.setAttribute('aria-hidden', 'false');
        updateNavBgHeight();
      }
    });
  };
  
  // Initial check for any dropdowns that might already be open (e.g., Webflow design mode or CSS hover)
  setTimeout(() => {
    checkForVisibleDropdowns();
    updateNavBgHeight();
  }, 100);
  
  // Also check immediately on mouse move in case user is hovering during page load
  let hasCheckedOnMouseMove = false;
  const nav = document.querySelector('.c-nav');
  if (nav) {
    const handleInitialMouseMove = () => {
      if (!hasCheckedOnMouseMove) {
        hasCheckedOnMouseMove = true;
        checkForVisibleDropdowns();
        nav.removeEventListener('mousemove', handleInitialMouseMove);
      }
    };
    nav.addEventListener('mousemove', handleInitialMouseMove, { once: true });
  }
}
initNavigation();